import { Grid, Switch, TextField } from "@mui/material";
import React from "react";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormDesignerComponentPropertiesWidth } from "./FormDesignerComponentPropertiesWidth";
import { Translations } from "ui/Scripts/source/models/translations";
import { TaskDataRowTotalType, TaskTypeClassWidthPrefix } from "ui/Scripts/source/models/enums";
import { FormAccordion } from "source/components/framework/FormAccordion";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { FormDesignerComponentPropertiesExpression } from "./FormDesignerComponentPropertiesExpression";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";

export const totalRowTypeList = [
    { id: TaskDataRowTotalType.None.toString(10), title: `'${Translations.SelectTaskDataRowTotalType}'` },
    { id: TaskDataRowTotalType.LineCount.toString(10), title: Translations.TaskDataRowTotalTypeLineCount },
    { id: TaskDataRowTotalType.ColSum.toString(10), title: Translations.TaskDataRowTotalTypeColSum },
    { id: TaskDataRowTotalType.ColAverage.toString(10), title: Translations.TaskDataRowTotalTypeColAverage },
    { id: TaskDataRowTotalType.Text.toString(10), title: Translations.TaskDataRowTotalTypeText },
    { id: TaskDataRowTotalType.Expression.toString(10), title: Translations.TaskDataRowTotalTypeExpression }
];

export interface ILineColumnWidth {
    field: string,
    webWidth: number
    mobileWidth: number
    printWidth: number,
    locked: boolean
};

interface IFormDesignerComponentPropertiesRowProps {
    fieldName?: string;
    formTypeCol?: ITaskTypeCol;
    lineTypeCol?: ITaskTypeCol;
    lineCols: ITaskTypeCol[];
    lineColumnWidths: ILineColumnWidth[];
    formFieldMargin: string;
    onAddFieldToLine: (id: string) => Promise<void>;
    onRemoveFieldFromLine: (id: string) => void;
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onRowLinePropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onChangeLineColumnWidth: (id: string, classes: string) => void;
}

export const FormDesignerComponentPropertiesRow = (
    props: IFormDesignerComponentPropertiesRowProps
) => {
    const handleSwitchShowInRow = () => {
        if (props.lineTypeCol) {
            props.onRemoveFieldFromLine(props.fieldName);
        } else {
            props.onAddFieldToLine(props.fieldName);
        }
    };

    // const defaultExpanded = !props.formTypeCol;

    // console.log("defaultExpanded", defaultExpanded);

    return (
        <FormAccordion
            defaultExpanded={false}
            title={Translations.FormRowColumn}
        >
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.FormShowInRow} margin={props.formFieldMargin}>
                        <Switch
                            name="readOnly"
                            checked={props.lineTypeCol !== undefined || false}
                            onChange={() => handleSwitchShowInRow()}
                        />
                    </FormField>
                </Grid>
                {props.lineTypeCol && (
                    <>
                        <Grid item sm={12}>
                            <FormDesignerComponentPropertiesWidth
                                classes={props.lineTypeCol?.class}
                                formFieldMargin={props.formFieldMargin}
                                sizePrefix={
                                    props.lineTypeCol?.class.includes(TaskTypeClassWidthPrefix.Web2)
                                        ? TaskTypeClassWidthPrefix.Web2
                                        : TaskTypeClassWidthPrefix.Web
                                }
                                id={props.lineTypeCol?.field}
                                minimum={0}
                                maximum={12 - props.lineColumnWidths.filter(i => i.webWidth > 0).length + 1}
                                title={Translations.FormColumnWidth}
                                onUpdate={props.onChangeLineColumnWidth}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <FormDesignerComponentPropertiesWidth
                                classes={props.lineTypeCol?.class}
                                formFieldMargin={props.formFieldMargin}
                                sizePrefix={TaskTypeClassWidthPrefix.Mobile}
                                id={props.lineTypeCol?.field}
                                minimum={0}
                                maximum={12 - props.lineColumnWidths.filter(i => i.mobileWidth > 0).length + 1}
                                title={Translations.FormColumnWidthMobile}
                                onUpdate={props.onChangeLineColumnWidth}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <FormDesignerComponentPropertiesWidth
                                classes={props.lineTypeCol?.class}
                                formFieldMargin={props.formFieldMargin}
                                sizePrefix={TaskTypeClassWidthPrefix.Print}
                                id={props.lineTypeCol?.field}
                                minimum={0}
                                maximum={12 - props.lineColumnWidths.filter(i => i.printWidth > 0).length + 1}
                                title={Translations.FormColumnWidthPrint}
                                onUpdate={props.onChangeLineColumnWidth}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <FormField title={Translations.FormColumnTitle} margin={props.formFieldMargin}>
                                <TextField
                                    value={props.lineTypeCol?.title}
                                    onChange={(e) =>
                                        props.onRowLinePropUpdate([{
                                            field: props.lineTypeCol?.field,
                                            name: "title",
                                            value: e.target.value
                                        }])
                                    }
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.FormSortField} margin={props.formFieldMargin}>
                                <Switch
                                    checked={props.lineTypeCol?.defaultSortField || false}
                                    onChange={(e, checked) =>
                                        props.onRowLinePropUpdate([{
                                            field: props.lineTypeCol?.field,
                                            name: "defaultSortField",
                                            value: checked
                                        }])
                                    }
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={6}>
                            <FormField title={Translations.FormSortAscending} margin={props.formFieldMargin}>
                                <Switch
                                    checked={props.lineTypeCol?.defaultSortOrderAsc || false}
                                    disabled={!props.lineTypeCol?.defaultSortField}
                                    onChange={(e, checked) =>
                                        props.onRowLinePropUpdate([{
                                            field: props.lineTypeCol?.field,
                                            name: "defaultSortOrderAsc",
                                            value: checked
                                        }])
                                    }
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={12}>
                            <FormField title={Translations.SummaryRow} margin={props.formFieldMargin}>
                                <IdTitleDropdown
                                    selectedId={(props.lineTypeCol?.lineTotalType ?? TaskDataRowTotalType.None).toString()}
                                    items={totalRowTypeList.map(i => { return { id: i.id, title: i.title }; })}
                                    sx={{ marginBottom: ".5rem" }}
                                    onChange={(id: string) => props.onRowLinePropUpdate([{
                                        field: props.lineTypeCol?.field,
                                        name: "lineTotalType",
                                        value: id.toInteger()
                                    }])}
                                />
                                {props.lineTypeCol?.lineTotalType === TaskDataRowTotalType.Text &&
                                    <TextField
                                        name="expression"
                                        title={Translations.Text}
                                        inputProps={{ maxLength: 100 }}
                                        value={props.lineTypeCol?.lineTotalExpression ?? ""}
                                        onChange={(event) => props.onRowLinePropUpdate([{
                                            field: props.lineTypeCol?.field,
                                            name: "lineTotalExpression",
                                            value: event.target.value
                                        }])}
                                        size="small"
                                        fullWidth
                                    />
                                }
                                {props.lineTypeCol?.lineTotalType === TaskDataRowTotalType.Expression &&
                                    <FormDesignerComponentPropertiesExpression
                                        formFieldMargin=".5rem 0 0 0"
                                        isRequired={false}
                                        show={true}
                                        field={props.lineTypeCol?.field}
                                        expression={props.lineTypeCol?.lineTotalExpression}
                                        expressionPropertyName="lineTotalExpression"
                                        expressionFormat={""}
                                        showExpressionFormat={false}
                                        taskTypeCols={props.lineCols}
                                        onTaskTypePropUpdate={props.onRowLinePropUpdate}
                                    />
                                }
                            </FormField>
                        </Grid>
                    </>
                )}
            </GridRow>
        </FormAccordion>
    );
};
